import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Check,
  ChevronsUpDown,
  Loader2,
  Mail,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import {
  EMPTY_MAPPING_FORM,
  createJobOwnerMapping,
  deleteJobOwnerMapping,
  emailFieldError,
  fetchAssignmentGroups,
  fetchJobOwnerMappings,
  formFromMapping,
  formatOwnerMappingError,
  payloadFromForm,
  splitEmails,
  updateJobOwnerMapping,
  validateMappingForm,
} from '@/lib/jobOwnerMappingsApi';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

function formatUpdated(mapping) {
  const when = mapping.updated_at ? new Date(mapping.updated_at) : null;
  const date = when && !Number.isNaN(when.getTime()) ? when.toLocaleString() : '—';
  return { date, by: mapping.updated_by || '' };
}

function sameName(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

function FieldError({ children, id }) {
  if (!children) return null;
  return (
    <p id={id} className="text-xs text-destructive mt-1" role="alert">
      {children}
    </p>
  );
}

function AssignmentGroupPicker({ value, groups, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const selectedMissing =
    Boolean(value) && groups.loaded && !groups.list.some((g) => sameName(g.name, value));

  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled || (groups.loading && !groups.loaded) || Boolean(groups.error && !value)}
              className="flex-1 justify-between font-normal"
              data-testid="owner-group-trigger"
            >
              <span className={value ? '' : 'text-muted-foreground'}>
                {value
                  || (groups.loading && !groups.loaded
                    ? 'Loading ServiceNow groups…'
                    : 'Select a ServiceNow assignment group')}
              </span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search groups…" />
              <CommandList>
                <CommandEmpty>No matching ServiceNow group.</CommandEmpty>
                <CommandGroup>
                  {groups.list.map((group) => (
                    <CommandItem
                      key={group.sys_id}
                      value={`${group.name} ${group.description || ''}`}
                      onSelect={() => {
                        onChange(group.name);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${sameName(group.name, value) ? 'opacity-100' : 'opacity-0'}`}
                      />
                      <div className="flex flex-col">
                        <span>{group.name}</span>
                        {group.description ? (
                          <span className="text-xs text-muted-foreground line-clamp-1">
                            {group.description}
                          </span>
                        ) : null}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange('')}
            aria-label="Clear assignment group"
            title="Clear assignment group"
          >
            <X className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
      {groups.error ? (
        <p className="text-xs text-amber-600" data-testid="owner-groups-error">
          Can&apos;t read ServiceNow groups right now ({groups.error}). You can still save an
          email-only owner, or{' '}
          <button type="button" className="underline" onClick={groups.reload}>
            try again
          </button>
          .
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          Only active groups from ServiceNow can be used.{' '}
          <button
            type="button"
            className="underline disabled:opacity-50"
            onClick={groups.refresh}
            disabled={groups.loading}
          >
            {groups.loading ? 'Refreshing…' : 'Refresh list'}
          </button>
        </p>
      )}
      {selectedMissing ? (
        <p className="text-xs text-amber-600" data-testid="owner-group-missing">
          &quot;{value}&quot; is no longer an active ServiceNow group. Pick another one or clear it.
        </p>
      ) : null}
    </div>
  );
}

export default function SettingsJobOwners() {
  const { isAdmin } = useAuth();

  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_MAPPING_FORM);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [groupList, setGroupList] = useState([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const loadMappings = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    setLoadError('');
    try {
      const params = { limit: 500 };
      if (debouncedQuery) params.q = debouncedQuery;
      const data = await fetchJobOwnerMappings(params);
      setMappings(data.mappings || []);
    } catch (err) {
      setLoadError(formatOwnerMappingError(err));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, debouncedQuery]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  const loadGroups = useCallback(async (refresh = false) => {
    setGroupsLoading(true);
    setGroupsError('');
    try {
      const data = await fetchAssignmentGroups({ refresh });
      setGroupList(data.groups || []);
      setGroupsLoaded(true);
    } catch (err) {
      setGroupsError(formatOwnerMappingError(err));
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  const groups = useMemo(
    () => ({
      list: groupList,
      loaded: groupsLoaded,
      loading: groupsLoading,
      error: groupsError,
      reload: () => loadGroups(false),
      refresh: () => loadGroups(true),
    }),
    [groupList, groupsLoaded, groupsLoading, groupsError, loadGroups],
  );

  const rows = useMemo(() => {
    const defaults = mappings.filter((m) => m.is_default);
    return [...defaults, ...mappings.filter((m) => !m.is_default)];
  }, [mappings]);

  const hasDefault = mappings.some((m) => m.is_default);
  const filtersActive = Boolean(debouncedQuery);
  const errors = validateMappingForm(form);
  const showError = (field) => (touched[field] || submitted ? errors[field] : '');

  const openDialog = (mapping = null, preset = null) => {
    setEditing(mapping);
    setForm(mapping ? formFromMapping(mapping) : { ...EMPTY_MAPPING_FORM, ...(preset || {}) });
    setTouched({});
    setSubmitted(false);
    setServerError('');
    setDialogOpen(true);
    if (!groupsLoaded && !groupsLoading) loadGroups(false);
  };

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setServerError('');
  };

  const touch = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const handleSave = async (event) => {
    event.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length) return;
    setSaving(true);
    setServerError('');
    try {
      const body = payloadFromForm(form);
      if (editing) {
        await updateJobOwnerMapping(editing.id, body);
        toast.success('Owner mapping updated');
      } else {
        await createJobOwnerMapping(body);
        toast.success('Owner mapping added');
      }
      setDialogOpen(false);
      await loadMappings();
    } catch (err) {
      setServerError(formatOwnerMappingError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (mapping, enabled) => {
    try {
      await updateJobOwnerMapping(mapping.id, {
        ...payloadFromForm(formFromMapping(mapping)),
        enabled,
      });
      setMappings((prev) => prev.map((m) => (m.id === mapping.id ? { ...m, enabled } : m)));
    } catch (err) {
      toast.error(formatOwnerMappingError(err));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteJobOwnerMapping(deleteTarget.id);
      toast.success('Owner mapping deleted');
      setDeleteTarget(null);
      await loadMappings();
    } catch (err) {
      toast.error(formatOwnerMappingError(err));
    } finally {
      setDeleting(false);
    }
  };

  if (!isAdmin) {
    return (
      <Card data-testid="settings-job-owners-denied">
        <CardContent className="py-12 text-center text-muted-foreground">
          Only administrators can view or change SAP job owners.
        </CardContent>
      </Card>
    );
  }

  const defaultTaken = hasDefault && !(editing && editing.is_default);
  const emailLive = touched.email || submitted ? emailFieldError(form.email) : '';

  return (
    <Card data-testid="settings-job-owners">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-cp-indigo" />
              SAP job owners
            </CardTitle>
            <CardDescription className="max-w-2xl">
              Who is told when a monitored SAP job fails. HALO finds the job&apos;s SAP
              application component text (for example &quot;Basis Components&quot;) and uses the
              contact set here for that owner. Owners without a mapping fall back to the
              default owner. Each owner needs an email, a ServiceNow assignment group, or both.
            </CardDescription>
          </div>
          <Button onClick={() => openDialog()} data-testid="add-owner-mapping">
            <Plus className="mr-2 h-4 w-4" />
            Add mapping
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!loading && !loadError && !hasDefault && !filtersActive ? (
          <Alert className="border-amber-400" data-testid="no-default-owner">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
              <span>
                There is no default owner. Failures for owners without a mapping have nobody to go to.
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => openDialog(null, { is_default: true })}
              >
                Set default owner
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search owner, email or group"
              className="pl-8"
              aria-label="Search owner mappings"
              data-testid="owner-search"
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={loadMappings}
            aria-label="Reload mappings"
            title="Reload"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          {!loading && !loadError ? (
            <span className="text-sm text-muted-foreground" data-testid="owner-count">
              {rows.length} {rows.length === 1 ? 'mapping' : 'mappings'}
            </span>
          ) : null}
        </div>

        {loadError ? (
          <Alert variant="destructive" data-testid="owner-load-error">
            <AlertDescription className="flex items-center justify-between gap-2">
              <span>{loadError}</span>
              <Button size="sm" variant="outline" onClick={loadMappings}>
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        ) : loading && !rows.length ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading owners…
          </div>
        ) : !rows.length ? (
          <div className="py-12 text-center" data-testid="owner-empty">
            <Users className="mx-auto mb-3 h-12 w-12 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold">
              {filtersActive ? 'No mappings match your search' : 'No job owners yet'}
            </h3>
            <p className="mb-4 text-muted-foreground">
              {filtersActive
                ? 'Try a different search or clear the filter.'
                : 'Start with a default owner, then add the owners you want to reach directly.'}
            </p>
            {!filtersActive ? (
              <Button onClick={() => openDialog(null, { is_default: true })}>
                <Plus className="mr-2 h-4 w-4" />
                Set default owner
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="rounded-md border">
            <Table data-testid="owner-grid">
              <TableHeader>
                <TableRow>
                  <TableHead>Owner (component text)</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead>Last changed</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((mapping) => {
                  const emails = splitEmails(mapping.email);
                  const updated = formatUpdated(mapping);
                  return (
                    <TableRow
                      key={mapping.id}
                      className={mapping.enabled ? '' : 'opacity-60'}
                      data-testid={`owner-row-${mapping.id}`}
                    >
                      <TableCell>
                        {mapping.is_default ? (
                          <div className="flex flex-col gap-1">
                            <Badge>Default owner</Badge>
                            <span className="text-sm text-muted-foreground">
                              Owners without a mapping
                            </span>
                          </div>
                        ) : (
                          <span className="font-medium">{mapping.owner}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 text-sm">
                          {emails.length ? (
                            <span
                              className="flex items-center gap-1"
                              title={emails.join(', ')}
                            >
                              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                              {emails[0]}
                              {emails.length > 1 ? (
                                <Badge variant="outline">+{emails.length - 1}</Badge>
                              ) : null}
                            </span>
                          ) : null}
                          {mapping.assignment_group ? (
                            <span className="flex items-center gap-1">
                              <Users className="h-3.5 w-3.5 text-muted-foreground" />
                              {mapping.assignment_group}
                            </span>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={mapping.enabled}
                          onCheckedChange={(value) => handleToggleEnabled(mapping, value)}
                          aria-label={`${mapping.enabled ? 'Disable' : 'Enable'} this mapping`}
                        />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        <div>{updated.date}</div>
                        {updated.by ? <div>{updated.by}</div> : null}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDialog(mapping)}
                          aria-label="Edit mapping"
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteTarget(mapping)}
                          aria-label="Delete mapping"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={(open) => !saving && setDialogOpen(open)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="owner-dialog">
          <form onSubmit={handleSave} noValidate className="space-y-5">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit owner mapping' : 'Add owner mapping'}</DialogTitle>
              <DialogDescription>
                Pick the owner, then say how to reach them.
              </DialogDescription>
            </DialogHeader>

            {serverError ? (
              <Alert variant="destructive" data-testid="owner-server-error">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            ) : null}

            <section className="space-y-3">
              <h4 className="text-sm font-semibold">Who owns it</h4>
              {form.is_default ? (
                <p className="rounded-md bg-muted p-3 text-sm" data-testid="owner-default-note">
                  This is the default owner. It is used when a job&apos;s owner has no mapping of
                  its own.
                </p>
              ) : (
                <div>
                  <Label htmlFor="owner-name">Owner</Label>
                  <Input
                    id="owner-name"
                    value={form.owner}
                    onChange={(e) => setField('owner', e.target.value)}
                    onBlur={() => touch('owner')}
                    placeholder="e.g. Basis Components"
                    aria-invalid={Boolean(showError('owner'))}
                    data-testid="owner-name"
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    The SAP application component text shown in the job&apos;s ownership analysis.
                    Not case sensitive.
                  </p>
                  <FieldError>{showError('owner')}</FieldError>
                </div>
              )}
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label htmlFor="owner-is-default">Default owner</Label>
                  <p className="text-xs text-muted-foreground">
                    {defaultTaken
                      ? 'A default owner already exists. Edit that one instead.'
                      : 'Use for every owner that has no mapping. There can be only one.'}
                  </p>
                </div>
                <Switch
                  id="owner-is-default"
                  checked={form.is_default}
                  onCheckedChange={(value) => setField('is_default', value)}
                  disabled={Boolean(editing) || defaultTaken}
                  data-testid="owner-is-default"
                />
              </div>
            </section>

            <section className="space-y-3">
              <div>
                <h4 className="text-sm font-semibold">How to reach them</h4>
                <p className="text-xs text-muted-foreground">
                  At least one is required: an email, a ServiceNow group, or both.
                </p>
              </div>
              <div>
                <Label htmlFor="owner-email">Email</Label>
                <Input
                  id="owner-email"
                  type="text"
                  inputMode="email"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  onBlur={() => touch('email')}
                  placeholder="basis-team@company.com"
                  aria-invalid={Boolean(emailLive)}
                  data-testid="owner-email"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  A team address or distribution list. Separate several with commas (up to 10).
                </p>
                <FieldError>{emailLive}</FieldError>
              </div>
              <div>
                <Label>ServiceNow assignment group</Label>
                <AssignmentGroupPicker
                  value={form.assignment_group}
                  groups={groups}
                  onChange={(name) => {
                    setField('assignment_group', name);
                    touch('assignment_group');
                  }}
                  disabled={saving}
                />
              </div>
              <FieldError id="owner-contact-error">{showError('contact')}</FieldError>
            </section>

            <section className="space-y-3">
              <div>
                <Label htmlFor="owner-notes">Notes (optional)</Label>
                <Textarea
                  id="owner-notes"
                  value={form.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                  rows={2}
                  placeholder="Anything the next admin should know"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label htmlFor="owner-enabled">Active</Label>
                  <p className="text-xs text-muted-foreground">
                    Turn off to keep the mapping without using it.
                  </p>
                </div>
                <Switch
                  id="owner-enabled"
                  checked={form.enabled}
                  onCheckedChange={(value) => setField('enabled', value)}
                />
              </div>
            </section>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="owner-save">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {editing ? 'Save changes' : 'Add mapping'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="owner-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this owner mapping?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `${deleteTarget.is_default ? 'The default owner' : deleteTarget.owner}. ${
                    deleteTarget.is_default
                      ? 'Owners without a mapping will have nobody to go to.'
                      : 'Its jobs will fall back to the default owner.'
                  }`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
              data-testid="owner-delete-confirm"
            >
              {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
